/**
 * @copyright (C) 2025, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Soyun Lee (sylee2@rsupport.com)
 *
 * @description
 *
 * @created at Fri May 23 2025
 **/

import { useCallback, useEffect } from "react";
import { useShallow } from "zustand/react/shallow";
import debounce from "lodash-es/debounce";
import { useTheme } from "@emotion/react";

import { EventHandler } from "@rfice/eventhandler";
import { _T } from "@rfice/language";

import ThreadMessageList from "@/modules/chat/components/Common/ThreadMessageList/ThreadMessageList";
import { useChatDataStore } from "@/modules/chat/store";
import { useChatMessageActions, useDMRoomState, useSpaceRoomState } from "@/frameworks/core/chat";
import { useRoomViewActions } from "@/frameworks/core/hooks";
import { Column, Icon, Row, Typography } from "@/frameworks/designStuff/components";
import { Properties } from "@/frameworks/libraries/properties";
import { EventID, ZIndex } from "@/frameworks/core/commons";
import { useFileUpload } from "@/frameworks/libraries";
import { MessageInput } from "../../../../../Common";

export default function FullSizeThread({
	channelID,
	threadID,
	spaceID,
	type,
	activityMessageID
}: {
	channelID: string;
	threadID: string;
	spaceID?: string;
	type: "DM" | "Space";
	activityMessageID?: string;
}) {
	const theme = useTheme();

	const { fullSizeChannelID, setFullSizeChannelID } = useChatDataStore(
		useShallow((state) => ({
			fullSizeChannelID: state.fullsize_chat_id,
			setFullSizeChannelID: state.set_fullsize_chat_id
		}))
	);

	const { threadReaded } = useChatMessageActions();
	const { updateRoomView } = useRoomViewActions();
	const spaceRoom = useSpaceRoomState({ spaceID: spaceID || "", roomID: channelID });
	const dmRoom = useDMRoomState(channelID);
	const { inputRef, drag, removeAll, reset } = useFileUpload({
		id: channelID,
		multiple: true
	});

	const channelData = spaceRoom ?? dmRoom;
	const { onDrop, ...rest } = drag.listeners;

	useEffect(() => {
		if (fullSizeChannelID && channelID !== fullSizeChannelID) {
			updateRoomView({
				roomId: fullSizeChannelID as string,
				deviceKey: Properties.browserTabID,
				view: false
			});
		}

		setFullSizeChannelID(channelID);
	}, [channelID]);

	const handleShowRecentThreadMessage = useCallback(
		debounce(() => {
			threadReaded({ roomId: channelID, threadMessageId: threadID });
		}, 1000),
		[dmRoom?.messageLastReadId]
	);

	const handleDrop = (ev: React.DragEvent<HTMLLabelElement>) => {
		EventHandler.emit(EventID.DROP_CHAT_FILES, {
			channelID: channelID,
			fileList: [...ev.dataTransfer.files],
			variant: "fullSize",
			isThread: true
		});
		onDrop(ev);
		removeAll();
		reset();
	};

	if (!channelData) return;
	const channelUserList = channelData.users ?? [];
	const userListForMention = channelUserList.map((user) => ({
		name: user.name,
		src: user.profileImagePath,
		userID: user.id
	}));

	return (
		<Column
			css={{ width: "100%", height: "100%", position: "relative", backgroundColor: theme.colors.neutral_8_100 }}
			onDrop={handleDrop}
			{...rest}
		>
			{drag.isDragging && <IsDragging />}
			<input type="file" id="file-upload" multiple={true} ref={inputRef} style={{ display: "none" }} />
			<ThreadMessageList
				variant="fullSize"
				roomID={channelID}
				isMyRoom={channelData?.myRoom}
				spaceID={spaceID}
				threadID={threadID}
				notice={channelData.notice}
				voteNotice={channelData.voteNotice}
				myRoomID={channelData.myRoomId}
				onShowRecentMessage={handleShowRecentThreadMessage}
				activityMessageID={activityMessageID}
			/>
			<MessageInput
				id={channelID}
				type={type}
				userList={userListForMention}
				variant="fullSize"
				myRoomID={channelData.myRoomId}
				isMyRoom={channelData.myRoom}
				threadID={threadID}
			/>
		</Column>
	);
}

function IsDragging() {
	const theme = useTheme();
	return (
		<Row
			verticalAlign="center"
			horizonAlign="center"
			gap={12}
			css={{
				position: "absolute",
				top: 0,
				left: 0,
				width: "100%",
				height: "100%",
				background: theme.colors.secondary_80_16,
				backdropFilter: "blur(14px)",
				zIndex: ZIndex.FULL_SIZE_CHAT + 1
			}}
		>
			<Typography typoColor="neutral_80_100" typoSize="Title1">
				{_T("파일 업로드")}
			</Typography>
			<Icon name="Upload24" fill={theme.colors.neutral_80_100} size={24} />
		</Row>
	);
}
